import { SORT_BY_STRING } from '@ubeya/shared/utils/sorting';

const GROUP_HEADER_STYLE = { fontWeight: 600 };
const NON_CLICKABLE_GROUP_HEADER_STYLE = { ...GROUP_HEADER_STYLE, cursor: 'default' };
const GROUPED_BRANCH_STYLE = { paddingLeft: 20 };
const NO_GROUP_KEY = 'none';

export const isBranchGroupHeaderOption = (option) => !!option?.isBranchGroupHeader;

export const getGroupedBranchOptionStyle = (option) => {
  if (isBranchGroupHeaderOption(option)) {
    return option.clickable === false ? NON_CLICKABLE_GROUP_HEADER_STYLE : GROUP_HEADER_STYLE;
  }
  return option?.branchGroupId !== undefined ? GROUPED_BRANCH_STYLE : undefined;
};

export const filterGroupedBranchOption = (option, search) =>
  option.value !== null && (option.searchText ?? option.label)?.toLowerCase()?.includes(search?.toLowerCase());

const getSearchText = (labels) => labels.join(' ');

const getNoGroupHeaderOption = (label, groupBranchesOptions) => ({
  value: `branch-group-header-${NO_GROUP_KEY}`,
  label,
  clickable: false,
  isBranchGroupHeader: true,
  searchText: getSearchText(groupBranchesOptions.map((option) => option.label))
});

const getGroupHeaderOption = ({ id, name }, groupBranchesOptions, isGroupSelectable) => ({
  value: isGroupSelectable ? -id : `branch-group-header-${id}`,
  label: name,
  ...(isGroupSelectable ? {} : { clickable: false }),
  isBranchGroupHeader: true,
  searchText: getSearchText([name, ...groupBranchesOptions.map((option) => option.label)])
});

const groupBranchesOptionsByGroupId = (branchesOptions, groupIds) =>
  branchesOptions.reduce((acc, option) => {
    const groupKey = groupIds.has(option.branchGroupId) ? option.branchGroupId : NO_GROUP_KEY;
    if (!acc.has(groupKey)) {
      acc.set(groupKey, []);
    }
    acc.get(groupKey).push(option);
    return acc;
  }, new Map());

export const getGroupedBranchesOptions = ({
  branchesOptions = [],
  branchGroups = [],
  noGroupLabel,
  isGroupSelectable = false,
  selectedBranchIds = []
}) => {
  const groupIds = new Set(branchGroups.map(({ id }) => id));
  const branchesOptionsByGroupId = groupBranchesOptionsByGroupId(branchesOptions, groupIds);

  const hasGroupedBranches = [...branchesOptionsByGroupId.keys()].some((groupKey) => groupKey !== NO_GROUP_KEY);
  if (!hasGroupedBranches) {
    return branchesOptions;
  }

  const selectedBranchIdsSet = new Set(selectedBranchIds);
  const hasUnselectedBranch = (options) => options.some(({ value }) => !selectedBranchIdsSet.has(value));

  const noGroupBranchesOptions = branchesOptionsByGroupId.get(NO_GROUP_KEY) || [];

  const groupsOptions = [...branchGroups].sort(SORT_BY_STRING('name')).flatMap((branchGroup) => {
    const groupBranchesOptions = branchesOptionsByGroupId.get(branchGroup.id) || [];
    if (groupBranchesOptions.length === 0) {
      return [];
    }
    return [
      ...(hasUnselectedBranch(groupBranchesOptions)
        ? [getGroupHeaderOption(branchGroup, groupBranchesOptions, isGroupSelectable)]
        : []),
      ...groupBranchesOptions.map((option) => ({
        ...option,
        searchText: getSearchText([option.label, branchGroup.name])
      }))
    ];
  });

  return [
    ...groupsOptions,
    ...(hasUnselectedBranch(noGroupBranchesOptions)
      ? [getNoGroupHeaderOption(noGroupLabel, noGroupBranchesOptions)]
      : []),
    ...noGroupBranchesOptions
  ];
};

export const extractBranchIdsOptions = (inputBranchIds = [], branchesOptions = []) => {
  const selectedGroupIds = new Set(inputBranchIds.filter((id) => typeof id === 'number' && id < 0).map((id) => -id));

  const branchesFromGroups = branchesOptions
    .filter(({ branchGroupId }) => selectedGroupIds.has(branchGroupId))
    .map(({ value }) => value);

  return [...new Set(inputBranchIds.filter((id) => typeof id !== 'number' || id > 0).concat(branchesFromGroups))];
};
